import React from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceRequest } from '../types';
import { formatDate } from '../utils/requestUtils';
import { 
  Dialog, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from "@/components/ui/dialog";
import { DialogContentWithoutCloseButton } from "@/components/ui/dialog-no-close";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/analyticsUtils";
import { CreditCard, RefreshCw } from 'lucide-react';
import { useProfileFetch } from "@/hooks/useProfileFetch";

interface ServiceDetailDialogProps {
  isOpen: boolean;
  request: ServiceRequest | null;
  onClose: () => void;
  onMarkAsPaid?: () => Promise<void>;
  onSendReminder?: (requestId: string) => Promise<{ success: boolean; error?: string; }>;
  onReschedule?: (requestId: string, newDate: Date) => Promise<void>;
}

const ServiceDetailDialog: React.FC<ServiceDetailDialogProps> = ({
  isOpen,
  request,
  onClose,
  onMarkAsPaid,
  onSendReminder,
  onReschedule
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const { profile } = useProfileFetch();

  const handleMarkAsPaid = async () => {
    if (!onMarkAsPaid) return;
    
    try {
      setIsProcessing(true);
      await onMarkAsPaid();
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
    };

    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles]}>
        {t(`status.${status}`)}
      </Badge>
    );
  };

  if (!request) return null;

  // Use user's preferred currency from profile, or fallback to USD
  const currency = profile?.currency || "USD";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContentWithoutCloseButton className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{request.services.name}</span>
            {getStatusBadge(request.status)}
          </DialogTitle>
          <DialogDescription>
            Service details and customer information
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 overflow-y-auto pb-16">
          <div>
            <h3 className="text-sm font-medium">Customer Information</h3>
            <p className="text-sm">{request.customer_name}</p>
            {request.customer_email && (
              <p className="text-sm text-gray-500">Email: {request.customer_email}</p>
            )}
            {request.customer_phone && (
              <p className="text-sm text-gray-500">Phone: {request.customer_phone}</p>
            )}
          </div>
          
          {request.location && (
            <div>
              <h3 className="text-sm font-medium">Location</h3>
              <p className="text-sm text-gray-500">{request.location}</p>
            </div>
          )}
          
          {request.scheduled_at && (
            <div>
              <h3 className="text-sm font-medium">Scheduled Date</h3>
              <p className="text-sm text-gray-500">{formatDate(request.scheduled_at)}</p>
            </div>
          )}
          
          <div>
            <h3 className="text-sm font-medium">Service Details</h3>
            <p className="text-sm">{request.services.name}</p>
            <p className="text-sm text-gray-500">
              Price: {formatCurrency(request.services.price, currency)}
            </p>
            <p className="text-sm text-gray-500">
              Status: {t(`status.${request.status}`)}
            </p>
            <p className="text-sm text-gray-500">
              Payment: <span className={request.paid ? "text-green-600" : "text-red-600"}>
                {request.paid ? "Paid" : "Unpaid"}
              </span>
            </p>
          </div>
          
          {request.notes && (
            <div>
              <h3 className="text-sm font-medium">Notes</h3>
              <p className="text-sm text-gray-500">{request.notes}</p>
            </div>
          )}
        </div>
        
        <div className="sticky bottom-0 left-0 right-0 bg-white z-10 border-t pt-3 pb-1 px-6">
          <div className="flex flex-col gap-2 w-full">
            {onMarkAsPaid && !request.paid && (
              <Button 
                onClick={handleMarkAsPaid}
                disabled={isProcessing}
                className="w-full"
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Mark as Paid
              </Button>
            )}
            
            {onSendReminder && request.status === 'accepted' && (
              <Button 
                onClick={() => request.id && onSendReminder(request.id)}
                disabled={isProcessing}
                className="w-full"
              >
                Send Reminder
              </Button>
            )}
            
            {onReschedule && ['accepted', 'pending'].includes(request.status) && (
              <Button 
                onClick={() => request.id && onReschedule(request.id, new Date())}
                disabled={isProcessing}
                className="w-full"
              >
                Reschedule
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContentWithoutCloseButton>
    </Dialog>
  );
};

export default ServiceDetailDialog;
